import { type FC, useEffect, useRef, useState, useCallback } from 'react';
import { Input, Alert, Tag, Typography } from 'antd';
import { Button } from '@g2u/ui-kit';
import {
  CloseOutlined,
  RobotOutlined,
  LikeOutlined,
  DislikeOutlined,
  LikeFilled,
  DislikeFilled,
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import { observer } from 'mobx-react-lite';
import cx from 'classnames';

import logoSmile from 'assets/images/geeks2u-logo-smile.svg';
import logoStarsAndSmile from 'assets/images/geeks2u-logo-stars-and-smile.svg';
import { useAiAssistant } from 'helpers/hooks/useAiAssistant';
import { useAiAssistantActions } from 'helpers/hooks/useAiAssistantActions';
import { CreateTicketModal } from 'components/common/create-ticket-modal';
import { chatActionRegistry } from './chat-actions';
import { DEFAULT_SHORT_CODES, ShortCodes } from './short-codes.component';
import styles from './ai-assistant.module.scss';

const { Text } = Typography;

export const AiAssistantContent: FC<{ isOpen?: boolean }> = observer(({ isOpen }) => {
  const [inputValue, setInputValue] = useState('');
  const [reasonInputs, setReasonInputs] = useState<Record<string, string>>({});
  const [selectedSuggestionLogId, setSelectedSuggestionLogId] = useState<number | null>(null);

  const {
    loading,
    conversation,
    errorMsg,
    customerProfile,
    job,
    feedbackState,
    submittedFeedbackDetails,
    suggestions,
    suggestionsLogId,
    loadingSuggestions,
    handlePostQuery,
    handleClearChat,
    handleFeedback,
    handleSubmitFeedbackDetails,
  } = useAiAssistant({ enabled: isOpen });

  const { isTicketModalOpen, setIsTicketModalOpen, handleCreateTicket, handleCallCustomer } = useAiAssistantActions();

  const [dismissedSuggestions, setDismissedSuggestions] = useState<string[]>([]);

  const suggestionsHash = suggestions?.join(',') ?? '';

  useEffect(() => {
    setDismissedSuggestions([]);
  }, [suggestionsHash]);

  const handleDismissSuggestion = (suggestionText: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDismissedSuggestions((prev) => [...prev, suggestionText]);
  };

  const visibleSuggestions = suggestions.filter((s) => !dismissedSuggestions.includes(s));

  const inputRef = useRef<any>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const handleSelectShortCode = useCallback((shortCodeText: string) => {
    setInputValue(shortCodeText);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleSuggestionClick = (suggestion: string) => {
    setInputValue(suggestion);
    setSelectedSuggestionLogId(suggestionsLogId);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  useEffect(() => {
    if (typeof chatEndRef.current?.scrollIntoView === 'function') {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation, loading]);


  const handleSubmit = () => {
    const textToSubmit = inputValue.trim();
    if (!textToSubmit) return;

    // Check if input matches short code format: /<category> <searchTerm>
    const shortCodeMatch = DEFAULT_SHORT_CODES.find(({ prefix }) => textToSubmit.startsWith(prefix));

    if (shortCodeMatch) {
      const category =  shortCodeMatch.category;
      const searchTerm = textToSubmit?.replace('/' + category, '').trim() || '';
      handlePostQuery(searchTerm || textToSubmit, category, selectedSuggestionLogId);
    } else {
      handlePostQuery(textToSubmit, '', selectedSuggestionLogId);
    }

    setInputValue('');
    setSelectedSuggestionLogId(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    // Standard Enter submit handling when shift key is not pressed
    if (e.key === 'Enter' && !e.shiftKey && inputValue.trim()) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className={styles.aiAssistant}>
      <div className={styles.aiAssistant__header}>
        <div className={styles.aiAssistant__headerTop}>
          <div className={styles.aiAssistant__headerLeft}>
            <img src={logoSmile} alt="Geeks2U Logo" />
            <span>AI Assistant</span>
          </div>
          <Button.$
            dataTestId="clear-chat"
            type={Button.Type.Link}
            className={styles.aiAssistant__headerLink}
            disabled={conversation.length === 0}
            onClick={() => {
              handleClearChat();
              setSelectedSuggestionLogId(null);
            }}
          >
            Clear chat
          </Button.$>
        </div>
        <div className={styles.aiAssistant__subtitle}>Helps you find answers from G2U Knowledge Base.</div>
      </div>

      <div className={styles.aiAssistant__content}>
        {conversation.length === 0 ? (
          <>
            <div className={styles.aiAssistant__welcome}>
              <img src={logoStarsAndSmile} alt="Geeks2U AI Assistant" className={styles.aiAssistant__welcomeLogo} />
              <h2 className={styles.aiAssistant__welcomeTitle}>Geeks2U AI Assistant</h2>
              <p className={styles.aiAssistant__welcomeGreeting}>Hi, I&apos;m Geeky 👋!</p>
              <p className={styles.aiAssistant__welcomeDescription}>
                I can help you quickly find answers from the Geeks2U Knowledge Base.
              </p>
            </div>

            <div className={styles.aiAssistant__info}>
              <h3 className={styles.aiAssistant__infoTitle}>A few things to keep in mind:</h3>
              <ul className={styles.aiAssistant__infoList}>
                <li>I only have access to approved knowledge base content.</li>
                <li>I can&apos;t view customer personal information, payment details, or other sensitive data.</li>
                <li>I may occasionally provide incomplete or incorrect answers.</li>
                <li>Please double-check important policies, pricing, and procedures before relying on my response.</li>
                <li>
                  When typing, use <code>/</code> for quick short codes (e.g., <code>/scripts</code> or{' '}
                  <code>/pricing</code>).
                </li>
              </ul>
            </div>
          </>
        ) : (
          <div className={styles.aiAssistant__chatArea}>
            {conversation.map((msg) => {
              const action = msg.action;

              return (
                <div
                  key={msg.id}
                  className={cx(styles.aiAssistant__messageRow, {
                    [styles.aiAssistant__messageRow_user]: msg.sender === 'user',
                    [styles.aiAssistant__messageRow_assistant]: msg.sender === 'assistant',
                  })}
                >
                  <div
                    className={cx(styles.aiAssistant__bubble, {
                      [styles.aiAssistant__bubble_user]: msg.sender === 'user',
                      [styles.aiAssistant__bubble_assistant]: msg.sender === 'assistant',
                    })}
                  >
                    {msg.sender === 'user' ? (
                      <Text className={styles.aiAssistant__userText}>{msg.text}</Text>
                    ) : (
                      <>
                        <div className={styles.aiAssistant__markdownContent}>
                          <ReactMarkdown>{msg.text}</ReactMarkdown>
                        </div>

                        {action && action.type && chatActionRegistry[action.type] && (
                          <div className={styles.aiAssistant__actions}>
                            <span className={styles.aiAssistant__actionsTitle}>SUGGESTED ACTION:</span>
                            <div className={styles.aiAssistant__actionsContent}>
                              {chatActionRegistry[action.type].render(action.payload, {
                                onCreateTicket: handleCreateTicket,
                                onCallCustomer: handleCallCustomer,
                              })}
                            </div>
                          </div>
                        )}

                        {msg.sourceLinks && msg.sourceLinks.length > 0 && (
                          <div className={styles.aiAssistant__sources}>
                            <span className={styles.aiAssistant__sourcesTitle}>SOURCES USED:</span>
                            <div className={styles.aiAssistant__sourcesContent}>
                              {msg.sourceLinks.map((source, index) => (
                                <Tag key={index} className={styles.aiAssistant__sourceTag}>
                                  <a href={source.url} target="_blank" rel="noopener noreferrer">
                                    {source.title}
                                  </a>
                                </Tag>
                              ))}
                            </div>
                          </div>
                        )}

                        {msg.log_id && (
                          <div className={styles.aiAssistant__feedback}>
                            {feedbackState[msg.log_id] !== false ? (
                              <>
                                <span className={styles.aiAssistant__feedbackLabel}>Is it a good answer?</span>
                                <div className={styles.aiAssistant__feedbackActions}>
                                  <span
                                    className={cx(styles.aiAssistant__feedbackButton, {
                                      [styles.aiAssistant__feedbackButton_yes_selected]:
                                        feedbackState[msg.log_id] === true,
                                    })}
                                    onClick={() => handleFeedback(msg?.log_id, true)}
                                  >
                                    {feedbackState[msg.log_id] === true ? <LikeFilled /> : <LikeOutlined />}
                                    <span className={styles.aiAssistant__feedbackButtonText}>Yes</span>
                                  </span>

                                  <span
                                    className={cx(styles.aiAssistant__feedbackButton, {
                                      [styles.aiAssistant__feedbackButton_no_selected]:
                                        feedbackState[msg.log_id] === false,
                                    })}
                                    onClick={() => handleFeedback(msg?.log_id, false)}
                                  >
                                    {feedbackState[msg.log_id] === false ? <DislikeFilled /> : <DislikeOutlined />}
                                    <span className={styles.aiAssistant__feedbackButtonText}>No</span>
                                  </span>
                                </div>
                              </>
                            ) : submittedFeedbackDetails[msg.log_id] !== true ? (
                              <div className={styles.aiAssistant__feedbackReasonContainer}>
                                <Input
                                  className={styles.aiAssistant__feedbackReasonInput}
                                  placeholder="Please tell us your reason"
                                  value={reasonInputs[msg.log_id] || ''}
                                  onChange={(e) =>
                                    setReasonInputs((prev) => ({
                                      ...prev,
                                      [msg.log_id]: e.target.value,
                                    }))
                                  }
                                  onPressEnter={(e) => {
                                    const val = reasonInputs[msg.log_id]?.trim();
                                    if (val) {
                                      e.preventDefault();
                                      handleSubmitFeedbackDetails(msg.log_id, val);
                                    }
                                  }}
                                />
                                <button
                                  className={cx(styles.aiAssistant__feedbackReasonSubmit, {
                                    [styles.aiAssistant__feedbackReasonSubmit_disabled]:
                                      !reasonInputs[msg.log_id]?.trim(),
                                  })}
                                  disabled={!reasonInputs[msg.log_id]?.trim()}
                                  onClick={() =>
                                    handleSubmitFeedbackDetails(msg.log_id, reasonInputs[msg.log_id].trim())
                                  }
                                >
                                  Send
                                </button>
                              </div>
                            ) : (
                              <div className={styles.aiAssistant__feedbackSubmittedContainer}>
                                <span className={styles.aiAssistant__feedbackSubmittedMessage}>
                                  Thanks for your feedback!
                                </span>
                                <span
                                  className={cx(
                                    styles.aiAssistant__feedbackButton,
                                    styles.aiAssistant__feedbackButton_no_selected_disabled,
                                  )}
                                >
                                  <DislikeFilled />
                                  <span className={styles.aiAssistant__feedbackButtonText}>No</span>
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}

            {loading && (
              <div className={cx(styles.aiAssistant__messageRow, styles.aiAssistant__messageRow_assistant)}>
                <div
                  className={cx(
                    styles.aiAssistant__bubble,
                    styles.aiAssistant__bubble_assistant,
                    styles.aiAssistant__loadingBubble,
                  )}
                >
                  <RobotOutlined className={styles.aiAssistant__loadingIcon} />
                  <span>Geeky is retrieving resources...</span>
                </div>
              </div>
            )}

            <div ref={chatEndRef} />
          </div>
        )}
      </div>

      <div className={styles.aiAssistant__footer}>
        {errorMsg && (
          <Alert
            message="Communication Issue"
            description={errorMsg}
            type="error"
            showIcon
            className={styles.aiAssistant__errorAlert}
          />
        )}

        {(loadingSuggestions || loading) && (
          <div className={styles.aiAssistant__suggestions}>
            {[1, 2, 3].map((item) => (
              <button
                key={item}
                type="button"
                className={cx(styles.aiAssistant__suggestionChip, styles.aiAssistant__suggestionChip_loading)}
                disabled
              />
            ))}
          </div>
        )}

        {!(loadingSuggestions || loading) && visibleSuggestions.length > 0 && (
          <div className={styles.aiAssistant__suggestions}>
            {visibleSuggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                className={styles.aiAssistant__suggestionChip}
                onClick={() => handleSuggestionClick(suggestion)}
              >
                <span>{suggestion}</span>
                <CloseOutlined
                  className={styles.aiAssistant__suggestionChipClose}
                  onClick={(e) => handleDismissSuggestion(suggestion, e)}
                />
              </button>
            ))}
          </div>
        )}

        <ShortCodes inputValue={inputValue} onSelectShortCode={handleSelectShortCode} onKeyDown={handleKeyDown}>
          <Input.TextArea
            ref={inputRef}
            className={styles.aiAssistant__footerInput}
            placeholder="e.g., How do I process a refund?"
            autoSize={{ minRows: 2, maxRows: 6 }}
            value={inputValue}
            disabled={loading}
            onChange={(e) => setInputValue(e.target.value)}
          />
        </ShortCodes>

        <Button.$
          dataTestId="ask-geeky"
          type={Button.Type.Primary}
          className={styles.aiAssistant__footerButton}
          disabled={!inputValue.trim() || loading}
          loading={loading}
          onClick={handleSubmit}
        >
          Ask Geeky
        </Button.$>
      </div>

      <CreateTicketModal
        customerId={customerProfile?.ID}
        jobId={job?.ID}
        isOpen={isTicketModalOpen}
        onClose={() => setIsTicketModalOpen(false)}
      />
    </div>
  );
});
